/**
 * Activity list filters (MOSA-879 / MOSA-368 slice 3).
 *
 * URL keeps calendar dates and filter keys. The API takes ISO 8601 instants
 * for `from` / `to`. Conversion uses the viewer's local time zone.
 *
 * API routes (prefix `/audit`, not `/datasources`):
 * - GET /audit/:id/activity?actor=&tab=&from=&to=&source=&q=&tz=&page=&limit=
 * - GET /audit/:id/activity/filters → { actors: [{ key, name, email }], tabs }
 * - GET /audit/:id/activity/export.csv (same filter params, including q and tz)
 *
 * `q` is also in the page URL. `tz` is not. The API call adds `tz`.
 * Truncation header: X-OnlyDash-Export-Truncated: true
 */

/** The only source value today. Google Sheets comes in a later slice. */
export const ACTIVITY_SOURCE_ONLYDASH = "ONLYDASH" as const;

export type ActivitySource = typeof ACTIVITY_SOURCE_ONLYDASH;

export type ActivityFilters = {
  actor?: string;
  tab?: string;
  /** Local calendar day YYYY-MM-DD. Sent to the API as start of that day. */
  from?: string;
  /** Local calendar day YYYY-MM-DD. Sent to the API as start of the next day. */
  to?: string;
  source?: ActivitySource;
  /** Search text. The API parses it. The page shows `interpreted`. */
  q?: string;
};

export type ActivityFilterActor = {
  key: string;
  name: string | null;
  email: string | null;
};

export type ActivityFilterOptions = {
  actors: ActivityFilterActor[];
  tabs: string[];
};

const DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

const isYmd = (value: string): boolean => DATE_YMD.test(value);

/**
 * Start of a local calendar day, as a UTC ISO instant.
 *
 * `from` on the API is inclusive.
 */
export const localDayStartIso = (dateYmd: string): string => {
  const [year, month, day] = dateYmd.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
};

/**
 * Start of the day after a local calendar day, as a UTC ISO instant.
 *
 * `to` on the API is exclusive.
 */
export const localDayAfterStartIso = (dateYmd: string): string => {
  const [year, month, day] = dateYmd.split("-").map(Number);
  return new Date(year, month - 1, day + 1, 0, 0, 0, 0).toISOString();
};

export const activityFiltersAreSet = (filters: ActivityFilters): boolean =>
  Boolean(
    filters.actor ||
      filters.tab ||
      filters.from ||
      filters.to ||
      filters.source ||
      filters.q?.trim()
  );

/** Read filter controls from the Activity URL query. */
export const activityFiltersFromParams = (
  params: URLSearchParams
): ActivityFilters => {
  const filters: ActivityFilters = {};
  const actor = params.get("actor")?.trim();
  if (actor) filters.actor = actor;
  const tab = params.get("tab")?.trim();
  if (tab) filters.tab = tab;
  const from = params.get("from")?.trim();
  if (from && isYmd(from)) filters.from = from;
  const to = params.get("to")?.trim();
  if (to && isYmd(to)) filters.to = to;
  const source = params.get("source")?.trim();
  if (source === ACTIVITY_SOURCE_ONLYDASH) {
    filters.source = ACTIVITY_SOURCE_ONLYDASH;
  }
  const q = params.get("q")?.trim();
  if (q) filters.q = q;
  return filters;
};

/** Write filter controls into a fresh URL query (no page). */
export const activityFiltersToParams = (
  filters: ActivityFilters
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.actor) params.set("actor", filters.actor);
  if (filters.tab) params.set("tab", filters.tab);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.source) params.set("source", filters.source);
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  return params;
};

/**
 * Build the query string for the Activity list or the CSV export.
 *
 * Local `from` / `to` dates become ISO instants. Paging is optional so the
 * export can send the same filters without `page` / `limit`.
 */
export const activityFiltersToApiQuery = (
  filters: ActivityFilters,
  paging?: { page?: number; limit?: number; timeZone?: string }
): string => {
  const params = new URLSearchParams();
  if (paging?.page != null) params.set("page", String(paging.page));
  if (paging?.limit != null) params.set("limit", String(paging.limit));
  if (filters.actor) params.set("actor", filters.actor);
  if (filters.tab) params.set("tab", filters.tab);
  if (filters.from) params.set("from", localDayStartIso(filters.from));
  if (filters.to) params.set("to", localDayAfterStartIso(filters.to));
  if (filters.source) params.set("source", filters.source);
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  // `tz` is an API param. Callers must not copy it into the page URL.
  if (paging?.timeZone?.trim()) params.set("tz", paging.timeZone.trim());
  return params.toString();
};

/** Label for a person in the Person select. */
export const activityActorLabel = (actor: ActivityFilterActor): string =>
  actor.name?.trim() || actor.email?.trim() || actor.key;

/**
 * File name from a Content-Disposition header.
 *
 * Prefers RFC 5987 `filename*`, then a quoted or bare `filename`.
 */
export const filenameFromContentDisposition = (
  header: string | undefined | null
): string | null => {
  if (!header) return null;
  const starred = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (starred) {
    try {
      return decodeURIComponent(starred[1].trim().replace(/["']/g, ""));
    } catch {
      return starred[1].trim().replace(/["']/g, "");
    }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted) return quoted[1].trim();
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare) return bare[1].trim().replace(/["']/g, "");
  return null;
};

/** Tolerant parse of the filters route body. */
export const asActivityFilterOptions = (
  body: unknown
): ActivityFilterOptions => {
  const raw = (body ?? {}) as Partial<ActivityFilterOptions>;
  const actors = Array.isArray(raw.actors)
    ? raw.actors
        .filter(
          (actor): actor is ActivityFilterActor =>
            !!actor && typeof (actor as ActivityFilterActor).key === "string"
        )
        .map((actor) => ({
          key: actor.key,
          name: actor.name ?? null,
          email: actor.email ?? null,
        }))
    : [];
  const tabs = Array.isArray(raw.tabs)
    ? raw.tabs.filter((tab): tab is string => typeof tab === "string")
    : [];
  return { actors, tabs };
};
